import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  Count,
  Handbook,
  HandbookCategory,
  HandbookCategoryDetails,
  HandbookCategoryResponseList,
  HandbookService,
  HandbookSubCategory,
  OpenAPI,
} from "@/api";
import resolve from "@/common/resolver";

type HandbookState = {
  categories: HandbookCategoryResponseList;
  handbook: Handbook | null;
  categoryDetails: HandbookCategoryDetails | null;
  handbookCount: Count | null;
};

const state: HandbookState = {
  categories: [],
  handbook: null,
  categoryDetails: null,
  handbookCount: null,
};

const getters: GetterTree<HandbookState, RootState> = {
  categories: state => state.categories,
  handbook: state => state.handbook,
  categoryDetails: state => state.categoryDetails,
  handbookCount: state => state.handbookCount,
};

const mutations: MutationTree<HandbookState> = {
  SET_HANDBOOK_COUNT: (state, count) => {
    state.handbookCount = count;
  },
  SET_CATEGORIES: (state, categories) => {
    state.categories = categories;
  },
  SET_HANDBOOK: (state, handbook) => {
    state.handbook = handbook;
  },
  SET_HANDBOOK_CATEGORY_DETAILS: (state, categoryDetails) => {
    state.categoryDetails = categoryDetails;
  },
};

const actions: ActionTree<HandbookState, RootState> = {
  fetchHandbookCount: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const count = await HandbookService.getHandbookCount({ companyId });
      commit("SET_HANDBOOK_COUNT", count);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchCategories: async ({ commit, rootState, getters }, force = false) => {
    const companyId = rootState.appContext.company?.id;
    let hasData = getters["categories"]?.length ? true : false;
    if (force) hasData = false;
    if (companyId && !hasData) {
      try {
        const categories = await HandbookService.getHandbookCategory({
          companyId,
        });
        commit("SET_CATEGORIES", categories);
      } catch (error) {
        if (error instanceof Error) {
          commit("alert/SET_SHOW_ERROR", error.message, { root: true });
        }
      }
    }
  },
  fetchCategoryDetails: async ({ commit, rootState }, categoryId: number) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId) {
      try {
        const details = await HandbookService.getHandbookCategoryDetails({
          companyId,
          categoryId,
        });
        commit("SET_HANDBOOK_CATEGORY_DETAILS", details);
      } catch (error) {
        if (error instanceof Error) {
          commit("alert/SET_SHOW_ERROR", error.message, { root: true });
        }
      }
    }
  },
  fetchHandbook: async (
    { commit, rootState },
    {
      categoryId,
      subCategoryId,
    }: { categoryId: number; subCategoryId?: number },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId) {
      try {
        const handbook = await HandbookService.getHandbook({
          companyId,
          categoryId,
          subCategoryId: subCategoryId ? subCategoryId : undefined,
        });
        commit("SET_HANDBOOK", handbook);
      } catch (error) {
        if (error instanceof Error) {
          commit("alert/SET_SHOW_ERROR", error.message, { root: true });
        }
      }
    }
  },
  addHandbookCategory: async (
    { commit, rootState, dispatch },
    category: HandbookCategory,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }
    try {
      category.companyId = companyId;
      await HandbookService.addHandbookCategory({
        companyId,
        requestBody: category,
      });
      dispatch("fetchCategories", true);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateHandbookCategory: async (
    { commit, rootState, dispatch },
    category: HandbookCategory,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }
    try {
      category.companyId = companyId;
      const res = await HandbookService.updateHandbookCategory({
        companyId,
        requestBody: category,
      });
      dispatch("fetchCategories", true);
      if (res.id) dispatch("fetchCategoryDetails", res.id);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteHandbookCategory: async (
    { commit, rootState, dispatch },
    category: HandbookCategory,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }
    try {
      category.companyId = companyId;
      await HandbookService.archiveHandbookCategory({
        companyId,
        requestBody: category,
      });
      dispatch("fetchCategories", true);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addHandbookSubCategory: async (
    { commit, rootState, dispatch },
    category: HandbookSubCategory,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }
    try {
      const res = await HandbookService.addHandbookSubcategory({
        companyId,
        requestBody: category,
      });
      dispatch("fetchCategories", true);
      dispatch("fetchCategoryDetails", res.categoryId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateHandbookSubCategory: async (
    { commit, rootState, dispatch },
    category: HandbookSubCategory,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }
    try {
      const res = await HandbookService.updateHandbookSubcategory({
        companyId,
        requestBody: category,
      });
      dispatch("fetchCategories", true);
      dispatch("fetchCategoryDetails", res.categoryId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteHandbookSubCategory: async (
    { commit, rootState, dispatch },
    category: HandbookSubCategory,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }
    try {
      const res = await HandbookService.archiveHandbookSubcategory({
        companyId,
        requestBody: category,
      });
      dispatch("fetchCategories", true);
      dispatch("fetchCategoryDetails", res.categoryId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addHandbook: async (
    { commit, rootState, dispatch },
    { handbook, file }: { handbook: Handbook; file: File },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file?.name);
    const requestBody = JSON.stringify(handbook);
    formData.append("handbookData", requestBody);

    try {
      await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/companies/${companyId}/handbook`,
        {
          method: "POST",
          body: formData,
          headers: {
            authorization: `Bearer ${await resolve(OpenAPI.TOKEN)}`,
          },
        },
      );
      dispatch("fetchCategoryDetails", handbook.categoryId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateHandbook: async (
    { commit, rootState, dispatch },
    { handbook, file }: { handbook: Handbook; file: File },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file?.name);
    const requestBody = JSON.stringify(handbook);
    formData.append("handbookData", requestBody);

    try {
      await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/companies/${companyId}/handbook`,
        {
          method: "PUT",
          body: formData,
          headers: {
            authorization: `Bearer ${await resolve(OpenAPI.TOKEN)}`,
          },
        },
      );
      dispatch("fetchCategoryDetails", handbook.categoryId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteHandbook: async (
    { commit, rootState, dispatch },
    requestBody: Handbook,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "No company selected", { root: true });
      return;
    }

    try {
      const res = await HandbookService.deleteHandbook({
        companyId,
        requestBody,
      });
      dispatch("fetchCategoryDetails", res.categoryId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const handbook: Module<HandbookState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
